import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users,
  UserPlus,
  Search,
  Download,
  Upload,
  Edit,
  Trash2,
  Calendar,
  Phone,
  Mail,
  FileText,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock,
  Crown,
  Lock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const ClientManagement = ({ token, user, onUpgradeClick }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [showAddClient, setShowAddClient] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Form states
  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    preferences: ""
  });
  
  const [appointmentForm, setAppointmentForm] = useState({
    client_id: "",
    service_name: "",
    date: "",
    time: "",
    duration_minutes: 60,
    price: 0,
    notes: ""
  });
  
  // Import/Export states
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Services for appointment creation
  const [services, setServices] = useState([]);
  
  // Check premium status
  const membership = user?.membership || {};
  const tier = membership.tier || "free";
  const isPremium = tier === "pro" || tier === "elite" || user?.email === "georgebroughton71@gmail.com";
  
  useEffect(() => {
    fetchClients();
    fetchServices();
  }, [token]);
  
  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients/my-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data.clients || []);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/stylists/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(res.data?.services || []);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    }
  };
  
  const handleAddClient = async () => {
    if (!clientForm.name) {
      toast.error("Client name is required");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/clients/my-clients`, clientForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client added successfully");
      setShowAddClient(false);
      setClientForm({ name: "", email: "", phone: "", notes: "", preferences: "" });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add client");
    }
  };
  
  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.put(`${API}/clients/my-clients/${selectedClient.client_id}`, clientForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client updated successfully");
      setShowEditClient(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update client");
    }
  };
  
  const handleDeleteClient = async (clientId) => {
    if (!confirm("Are you sure you want to delete this client?")) return;
    
    try {
      await axios.delete(`${API}/clients/my-clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client deleted successfully");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };
  
  const handleCreateAppointment = async () => {
    if (!appointmentForm.client_id || !appointmentForm.service_name || !appointmentForm.date || !appointmentForm.time) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await axios.post(`${API}/clients/appointments`, appointmentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appointment created successfully");
      setShowAddAppointment(false);
      setAppointmentForm({
        client_id: "",
        service_name: "",
        date: "",
        time: "",
        duration_minutes: 60,
        price: 0,
        notes: ""
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    }
  };
  
  const handleExport = async (format) => {
    if (!isPremium) {
      toast.error("Export is a Premium feature. Please upgrade to Pro or Elite.");
      onUpgradeClick?.();
      return;
    }
    
    setExporting(true);
    try {
      const res = await axios.get(`${API}/clients/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (format === "csv") {
        // Download CSV
        const blob = new Blob([res.data.content], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (format === "excel") {
        // For Excel, we need to use a library like xlsx on the frontend
        // For now, export as JSON that user can import into Excel
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename.replace('.xlsx', '.json');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported ${res.data.total} clients`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export clients");
    } finally {
      setExporting(false);
    }
  };
  
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!isPremium) {
      toast.error("Import is a Premium feature. Please upgrade to Pro or Elite.");
      onUpgradeClick?.();
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/clients/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import clients");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const openEditClient = (client) => {
    setSelectedClient(client);
    setClientForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      notes: client.notes || "",
      preferences: client.preferences || ""
    });
    setShowEditClient(true);
  };
  
  const openAddAppointment = (client) => {
    setAppointmentForm({
      ...appointmentForm,
      client_id: client.client_id
    });
    setShowAddAppointment(true);
  };
  
  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.phone?.includes(query)
    );
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-500" />
            Client Management
          </h2>
          <p className="text-stone-400 text-sm mt-1">
            Manage your clients, add appointments, and import/export your client list
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowAddClient(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="add-client-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
          
          {/* Import Button */}
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImport}
              className="hidden"
              id="import-file"
            />
            <Button
              variant="outline"
              onClick={() => {
                if (!isPremium) {
                  toast.error("Import is a Premium feature");
                  onUpgradeClick?.();
                  return;
                }
                fileInputRef.current?.click();
              }}
              disabled={importing}
              className={`border-stone-600 text-stone-300 hover:bg-stone-800 ${!isPremium ? 'opacity-60' : ''}`}
              data-testid="import-clients-btn"
            >
              {importing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import
              {!isPremium && <Lock className="w-3 h-3 ml-1" />}
            </Button>
          </div>
          
          {/* Export Button */}
          <Select onValueChange={handleExport} disabled={exporting}>
            <SelectTrigger 
              className={`w-[130px] border-stone-600 bg-transparent text-stone-300 ${!isPremium ? 'opacity-60' : ''}`}
              data-testid="export-clients-btn"
            >
              <SelectValue placeholder={
                <span className="flex items-center gap-2">
                  {exporting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Export
                  {!isPremium && <Lock className="w-3 h-3" />}
                </span>
              } />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">Export as CSV</SelectItem>
              <SelectItem value="excel">Export as Excel</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Premium Banner for Free Users */}
      {!isPremium && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-amber-500" />
              <div>
                <p className="text-white font-medium">Unlock Import/Export</p>
                <p className="text-stone-400 text-sm">Upgrade to Pro or Elite to import clients from other platforms</p>
              </div>
            </div>
            <Button 
              onClick={onUpgradeClick}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Import Result */}
      {importResult && (
        <Card className={`${importResult.errors?.length > 0 ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {importResult.errors?.length > 0 ? (
                <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              )}
              <div>
                <p className="text-white font-medium">{importResult.message}</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-yellow-400 text-sm mb-1">Warnings:</p>
                    <ul className="text-stone-400 text-sm space-y-1">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                      {importResult.errors.length > 5 && (
                        <li>• ...and {importResult.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportResult(null)}
                className="ml-auto text-stone-400"
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
        <Input
          placeholder="Search clients by name, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-stone-800 border-stone-700 text-white"
          data-testid="client-search-input"
        />
      </div>
      
      {/* Client List */}
      <div className="grid gap-4">
        {filteredClients.length === 0 ? (
          <Card className="bg-stone-800/50 border-stone-700">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-stone-600 mx-auto mb-4" />
              <p className="text-white font-medium mb-2">No clients yet</p>
              <p className="text-stone-400 text-sm mb-4">
                Add your first client or import from another platform
              </p>
              <Button
                onClick={() => setShowAddClient(true)}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Your First Client
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredClients.map((client) => (
            <Card key={client.client_id} className="bg-stone-800/50 border-stone-700 hover:border-stone-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <span className="text-amber-500 font-bold text-lg">
                        {client.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{client.name}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-stone-400 mt-1">
                        {client.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {client.email}
                          </span>
                        )}
                        {client.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {client.phone}
                          </span>
                        )}
                        {client.last_visit_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last visit: {client.last_visit_date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`
                      ${client.source === 'manual' ? 'border-blue-500/50 text-blue-400' : 
                        client.source === 'imported' ? 'border-purple-500/50 text-purple-400' : 
                        'border-green-500/50 text-green-400'}
                    `}>
                      {client.source === 'manual' ? 'Manual' : 
                       client.source === 'imported' ? 'Imported' : 'Booking'}
                    </Badge>
                    
                    {client.total_visits > 0 && (
                      <Badge className="bg-amber-500/20 text-amber-400">
                        {client.total_visits} visits
                      </Badge>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openAddAppointment(client)}
                      className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                      data-testid={`book-client-${client.client_id}`}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Book
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditClient(client)}
                      className="text-stone-400 hover:text-white"
                      data-testid={`edit-client-${client.client_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    {client.source === 'manual' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClient(client.client_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-client-${client.client_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {(client.notes || client.preferences) && (
                  <div className="mt-3 pt-3 border-t border-stone-700">
                    {client.notes && (
                      <p className="text-stone-400 text-sm">
                        <FileText className="w-3 h-3 inline mr-1" />
                        {client.notes}
                      </p>
                    )}
                    {client.preferences && (
                      <p className="text-stone-500 text-sm mt-1">
                        Preferences: {client.preferences}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Add Client Modal */}
      <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-500" />
              Add New Client
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Add a client manually to your list
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-stone-300">Name *</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                placeholder="Client name"
                className="bg-stone-800 border-stone-700 text-white mt-1"
                data-testid="client-name-input"
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Email</Label>
              <Input
                type="email"
                value={clientForm.email}
                onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                placeholder="client@email.com"
                className="bg-stone-800 border-stone-700 text-white mt-1"
                data-testid="client-email-input"
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Phone</Label>
              <Input
                value={clientForm.phone}
                onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                placeholder="(555) 123-4567"
                className="bg-stone-800 border-stone-700 text-white mt-1"
                data-testid="client-phone-input"
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Notes</Label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                placeholder="Any notes about this client..."
                className="bg-stone-800 border-stone-700 text-white mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Preferences</Label>
              <Input
                value={clientForm.preferences}
                onChange={(e) => setClientForm({...clientForm, preferences: e.target.value})}
                placeholder="Hair type, favorite styles, etc."
                className="bg-stone-800 border-stone-700 text-white mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClient(false)} className="border-stone-600">
              Cancel
            </Button>
            <Button onClick={handleAddClient} className="bg-amber-500 hover:bg-amber-600 text-black">
              Add Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Client Modal */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-500" />
              Edit Client
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-stone-300">Name *</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Email</Label>
              <Input
                type="email"
                value={clientForm.email}
                onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Phone</Label>
              <Input
                value={clientForm.phone}
                onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Notes</Label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-stone-300">Preferences</Label>
              <Input
                value={clientForm.preferences}
                onChange={(e) => setClientForm({...clientForm, preferences: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditClient(false)} className="border-stone-600">
              Cancel
            </Button>
            <Button onClick={handleUpdateClient} className="bg-amber-500 hover:bg-amber-600 text-black">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Appointment Modal */}
      <Dialog open={showAddAppointment} onOpenChange={setShowAddAppointment}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              Create Appointment
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Schedule an appointment for this client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-stone-300">Service *</Label>
              <Select
                value={appointmentForm.service_name}
                onValueChange={(value) => {
                  const service = services.find(s => s.name === value);
                  setAppointmentForm({
                    ...appointmentForm,
                    service_name: value,
                    duration_minutes: service?.duration_minutes || 60,
                    price: service?.price || 0
                  });
                }}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700 text-white mt-1">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.service_id || service.name} value={service.name}>
                      {service.name} - ${service.price}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {appointmentForm.service_name === "custom" && (
              <div>
                <Label className="text-stone-300">Custom Service Name</Label>
                <Input
                  value={appointmentForm.service_name === "custom" ? "" : appointmentForm.service_name}
                  onChange={(e) => setAppointmentForm({...appointmentForm, service_name: e.target.value})}
                  placeholder="Enter service name"
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Date *</Label>
                <Input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({...appointmentForm, date: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-stone-300">Time *</Label>
                <Input
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({...appointmentForm, time: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Duration (mins)</Label>
                <Input
                  type="number"
                  value={appointmentForm.duration_minutes}
                  onChange={(e) => setAppointmentForm({...appointmentForm, duration_minutes: parseInt(e.target.value)})}
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-stone-300">Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={appointmentForm.price}
                  onChange={(e) => setAppointmentForm({...appointmentForm, price: parseFloat(e.target.value)})}
                  className="bg-stone-800 border-stone-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-stone-300">Notes</Label>
              <Textarea
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({...appointmentForm, notes: e.target.value})}
                placeholder="Any special notes for this appointment..."
                className="bg-stone-800 border-stone-700 text-white mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAppointment(false)} className="border-stone-600">
              Cancel
            </Button>
            <Button onClick={handleCreateAppointment} className="bg-amber-500 hover:bg-amber-600 text-black">
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientManagement;
